/**
 * Single source of truth for data-import upload types, column headers, and sample rows.
 * Used for template generation, validation, and the upload-type picker.
 *
 * All date columns use DD/MM/YYYY (example: 06/06/2026).
 */

export const STANDARD_DATE_FORMAT = 'DD/MM/YYYY';
export const STANDARD_DATE_EXAMPLE = '06/06/2026';
export const DATE_FIELD_STANDARD = 'Date (DD/MM/YYYY)';

export const DATA_UPLOAD_TEMPLATES = {
  chartofaccounts: {
    label: 'Chart of accounts upload',
    filename: 'chart_of_accounts_template',
    onboardingOnly: true,
    headers: ['Account name', 'Acc code', 'Chart of account category', 'Chart of account subcategory', 'Currency'],
    requiredFields: ['Account name', 'Acc code', 'Chart of account category', 'Chart of account subcategory'],
    dateFields: [],
    sampleRow: ['CASH A/C', '1001', 'Assets', 'Cash', 'UGX'],
    hint: 'Create institutional ledger accounts before running operations.',
  },
  singlemembers: {
    label: 'Individual customer accounts upload',
    filename: 'single_members_template',
    onboardingOnly: true,
    headers: [
      'No', 'Surname', 'Othername', 'MiddleName', 'Gender', 'Nationality',
      'Date of birth (DD/MM/YYYY)', 'Phone number', 'Email', 'Area of residence',
      'member type', 'Date of joining (DD/MM/YYYY)', 'ID type', 'Id number', 'Member ID',
    ],
    dateFields: ['Date of birth (DD/MM/YYYY)', 'Date of joining (DD/MM/YYYY)'],
    requiredFields: ['Surname', 'Othername', 'member type', 'ID type', 'Member ID'],
    sampleRow: ['1', 'Doe', 'John', 'K', 'Male', 'Ugandan', '10/06/1990', '+256700000000', 'john@example.com', 'Kampala', 'Ordinary', '01/01/2024', 'National ID', 'CM123456', 'M001'],
    hint: 'Account type, ID type, and district must already exist in the system.',
  },
  groups: {
    label: 'Group accounts upload',
    filename: 'groups_template',
    onboardingOnly: true,
    headers: [
      'No', 'Group name', 'District', 'Subcounty', 'Parish', 'Village',
      'Membertype', 'MemberId', 'No of Members', 'No of Males', 'No of Females',
      'First contact', 'Date of joining (DD/MM/YYYY)',
    ],
    requiredFields: ['Group name', 'Membertype', 'MemberId'],
    dateFields: ['Date of joining (DD/MM/YYYY)'],
    sampleRow: ['1', 'Farmers Group', 'Wakiso', 'Kira', 'Kira', 'Kira A', 'Group', 'G001', '10', '5', '5', '+256700000001', '01/01/2024'],
    hint: 'Group account type and district must already exist.',
  },
  loans: {
    label: 'Running Loans upload',
    filename: 'running_loans_template',
    onboardingOnly: true,
    headers: [
      DATE_FIELD_STANDARD, 'Amount', 'Period', 'Loan type', 'Account no',
      'Principle balance', 'Interest balance', 'Fine balance', 'LoanNo',
      'Register Incomes', 'IncomeRegistrationMode(FromLoan,Fromsavings,OnlyRegister)',
      'bankcharges', 'Onboarding date (DD/MM/YYYY)',
    ],
    requiredFields: [DATE_FIELD_STANDARD, 'Amount', 'Period', 'Loan type', 'Account no'],
    dateFields: [DATE_FIELD_STANDARD, 'Onboarding date (DD/MM/YYYY)'],
    sampleRow: ['01/01/2024', '2000000', '12', 'BUSINESS LOAN', 'M001', '1800000', '200000', '0', 'L001', 'Yes', 'FromLoan', '0', '01/06/2024'],
    hint: 'Partially paid loan: Amount = original loan; Principle balance & Interest balance = what the customer still owes on cutover date; Onboarding date = cutover date. Leave balances at 0 only for brand-new loans (Path B — then import repayments separately).',
  },
  expenses: {
    label: 'Expenses upload',
    filename: 'expenses_template',
    onboardingOnly: true,
    headers: ['No', 'ExpenseAccount', DATE_FIELD_STANDARD, 'Amount', 'CreditAccount', 'Desc', 'Account no', 'Account name', 'JournalNo', 'User/Teller'],
    requiredFields: ['ExpenseAccount', DATE_FIELD_STANDARD, 'Amount', 'CreditAccount'],
    dateFields: [DATE_FIELD_STANDARD],
    sampleRow: ['1', 'Rent and Utilities', '01/01/2024', '500000', 'CASH A/C', 'Office rent', '', '', '', 'demouser'],
    hint: 'Expense and credit accounts must exist in chart of accounts.',
  },
  incomes: {
    label: 'Other Incomes (None Loan) upload',
    filename: 'incomes_template',
    onboardingOnly: true,
    headers: ['No', 'IncomeAccount', DATE_FIELD_STANDARD, 'Amount', 'DebitAccount', 'Desc', 'Account no', 'Account name', 'JournalNo', 'User/Teller'],
    requiredFields: ['IncomeAccount', DATE_FIELD_STANDARD, 'Amount', 'DebitAccount'],
    dateFields: [DATE_FIELD_STANDARD],
    allowExcelDateSerial: true,
    sampleRow: ['1', 'Interest on Loans', '01/01/2024', '100000', 'CASH A/C', 'Interest received', '', '', '', 'demouser'],
    hint: 'Income and debit accounts must exist in chart of accounts.',
  },
  deposits: {
    label: 'Opening deposits (savings, shares, reserve) upload',
    filename: 'opening_deposits_template',
    onboardingOnly: true,
    headers: [DATE_FIELD_STANDARD, 'MemberId', 'Savings', 'Shares', 'SavingsReserve', 'Transaction details'],
    requiredFields: [DATE_FIELD_STANDARD, 'MemberId'],
    dateFields: [DATE_FIELD_STANDARD],
    allowExcelDateSerial: true,
    sampleRow: ['01/01/2024', 'M001', '500000', '100000', '0', 'Opening balance import'],
    hint: 'Customer account must already exist (MemberId = account number).',
  },
  loanschedules: {
    label: 'Loan installment schedules upload',
    filename: 'loan_schedules_template',
    onboardingOnly: true,
    headers: [
      'LoanNo', 'Repayment number', 'Installment date (DD/MM/YYYY)',
      'Principle', 'Interest', 'Total', 'Balance',
    ],
    requiredFields: ['LoanNo', 'Repayment number', 'Installment date (DD/MM/YYYY)', 'Principle', 'Interest'],
    dateFields: ['Installment date (DD/MM/YYYY)'],
    allowExcelDateSerial: true,
    sampleRow: ['L001', '1', '01/02/2024', '166667', '60000', '226667', '1833333'],
    hint: 'Import after running loans. Replaces auto-generated schedule per LoanNo. One row per installment from your old system.',
  },
  loanrepayments: {
    label: 'Loan repayments upload',
    filename: 'loan_repayments_template',
    onboardingOnly: true,
    headers: ['LoanId', DATE_FIELD_STANDARD, 'Principle', 'Interest', 'Fine', 'SavingsTransfer(Yes/No)', 'JournalNo', 'User/Teller', 'receiver_account'],
    requiredFields: ['LoanId', DATE_FIELD_STANDARD],
    dateFields: [DATE_FIELD_STANDARD],
    sampleRow: ['L001', '01/02/2024', '100000', '20000', '0', 'No', '', 'demouser', 'CASH A/C'],
    hint: 'LoanId = LoanNo from running loans (e.g. L001), not database ID. Only use when importing full payment history (Path B). Skip if you used Principle/Interest balance on running loans (Path A).',
  },
  multitransactions: {
    label: 'Savings/shares/Reserve (Multi Transactions) opening balance upload',
    filename: 'multi_transactions_template',
    onboardingOnly: true,
    headers: [
      'No', DATE_FIELD_STANDARD, 'AccountNo', 'AccountName', 'debit', 'credit', 'transacted_by',
      'transactor_contact', 'user', 'computer', 'transaction_details', 'subaccount',
      'transaction_from', 'journal_no', 'receiver_account', 'pay_account',
    ],
    dateFields: [DATE_FIELD_STANDARD],
    allowExcelDateSerial: true,
    sampleRow: ['1', '01/01/2024', 'M001', 'John Doe', '0', '50000', 'John Doe', '+256700000000', 'demouser', '', 'Opening savings', 'savings', '', '', '', ''],
    hint: 'Subaccount: savings, shares, etc.',
  },
  sent_smsmessages: {
    label: 'SMS sent upload',
    filename: 'sms_messages_template',
    onboardingOnly: true,
    headers: [DATE_FIELD_STANDARD, 'message', 'number'],
    dateFields: [DATE_FIELD_STANDARD],
    allowExcelDateSerial: true,
    sampleRow: ['01/01/2024', 'Welcome message', '+256700000000'],
    hint: 'Historical SMS log import.',
  },
  system_users: {
    label: 'System users',
    filename: 'system_users_template',
    onboardingOnly: true,
    headers: ['email', 'username', 'is_admin', 'is_active', 'is_staff', 'is_superuser', 'branch_manager', 'password'],
    dateFields: [],
    sampleRow: ['user@example.com', 'newuser', 'false', 'true', 'true', 'false', 'false', 'ChangeMe123'],
    hint: 'Boolean fields: true or false.',
  },
  districts: {
    label: 'Ugandan districts',
    filename: 'ugandan_districts_template',
    onboardingOnly: true,
    headers: ['NO', 'REGION', 'DISTRICT'],
    dateFields: [],
    sampleRow: ['1', 'Central', 'Kampala'],
    hint: 'Bulk import Ugandan districts.',
  },
  operationalsavings_sharesdeposits: {
    label: 'Savings/shares upload',
    filename: 'operational_deposits_template',
    onboardingOnly: false,
    headers: [
      DATE_FIELD_STANDARD, 'MemberId', 'Savings', 'SharesNO', 'LoanID', 'LoanPrinciple',
      'LoanInterest', 'LoanFromSavings(Yes/No)', 'TransactionDetails', 'ReceivingAccount',
    ],
    dateFields: [DATE_FIELD_STANDARD],
    sampleRow: ['01/06/2024', 'M001', '50000', '10', '', '', '', 'No', 'Monthly savings', 'CASH A/C'],
    hint: 'Operational deposit after onboarding. ReceivingAccount is the cash/bank GL account.',
  },
  operationalsavings_withdraws: {
    label: 'Savings withdraw upload',
    filename: 'operational_withdraws_template',
    onboardingOnly: false,
    headers: [
      DATE_FIELD_STANDARD, 'MemberId', 'WithdrawAmount', 'bankcharges',
      'BankChargefrom(Savings/Expense)', 'BankChargeExpenseAccount', 'Transaction details',
      'CreditAccount', 'Withdrawcharge',
    ],
    dateFields: [DATE_FIELD_STANDARD],
    sampleRow: ['01/06/2024', 'M001', '20000', '0', 'Savings', '', 'Member withdrawal', 'CASH A/C', '0'],
    hint: 'Operational withdrawal after onboarding.',
  },
  loanopeningbalances: {
    label: 'Loan opening balances upload',
    filename: 'loan_opening_balances_template',
    onboardingOnly: false,
    headers: ['MemberID', 'LoanID', 'PrincipleBalance', 'interestBalance', 'Opening balance date (DD/MM/YYYY)'],
    requiredFields: ['MemberID', 'LoanID', 'PrincipleBalance', 'interestBalance', 'Opening balance date (DD/MM/YYYY)'],
    dateFields: ['Opening balance date (DD/MM/YYYY)'],
    sampleRow: ['M001', '1', '1500000', '150000', '01/01/2024'],
    hint: 'Customer account and loan must already exist. Used to set opening principle/interest balances.',
  },
  coa_opening_balances: {
    label: 'COA / trial balance opening balances upload',
    filename: 'coa_opening_balances_template',
    onboardingOnly: false,
    headers: [
      DATE_FIELD_STANDARD, 'Account', 'Description', 'Debit', 'Credit', 'JournalNo', 'User',
    ],
    requiredFields: [DATE_FIELD_STANDARD, 'Account'],
    dateFields: [DATE_FIELD_STANDARD],
    allowExcelDateSerial: true,
    sampleRow: ['01/01/2024', 'Office Equipment', 'Opening balance', '250000', '0', '', 'demouser'],
    hint: 'Post opening debit/credit for other GL accounts (assets, liabilities, equity, income, expense). Member savings/shares/loans use their own uploads. Each journal must balance (total debits = total credits). Same JournalNo = one journal; blank JournalNo = one auto journal for the whole file. Account accepts GL name or code.',
  },
  special_journal_entries: {
    label: 'Missing GL journals upload',
    filename: 'missing_journals_template',
    onboardingOnly: false,
    headers: [DATE_FIELD_STANDARD, 'Account', 'Description', 'JournalNo', 'User', 'Credit', 'Debit'],
    requiredFields: [DATE_FIELD_STANDARD, 'Account', 'Description', 'JournalNo', 'Credit', 'Debit'],
    dateFields: [DATE_FIELD_STANDARD],
    sampleRow: ['06/06/2026', 'CASH A/C', 'Back-fill missing journal', '7', 'demouser', '0', '60000'],
    hint: 'Creates GeneralJournal rows for existing customer transactions that are missing GL entries. JournalNo must match an existing transaction date.',
  },
};

/** Upload types shown when client onboarding is not yet complete. */
export function getOnboardingUploadTypes() {
  return Object.entries(DATA_UPLOAD_TEMPLATES)
    .filter(([, cfg]) => cfg.onboardingOnly)
    .map(([key, cfg]) => ({ value: key, label: cfg.label }));
}

/** Upload types always available (post-onboarding operational imports). */
export function getOperationalUploadTypes() {
  return Object.entries(DATA_UPLOAD_TEMPLATES)
    .filter(([, cfg]) => !cfg.onboardingOnly)
    .map(([key, cfg]) => ({ value: key, label: cfg.label }));
}

export function getTemplateConfig(uploadType) {
  return DATA_UPLOAD_TEMPLATES[uploadType] || null;
}

export function getAllTemplateList() {
  return Object.entries(DATA_UPLOAD_TEMPLATES).map(([key, cfg]) => ({
    uploadtype: key,
    label: cfg.label,
    filename: cfg.filename,
    headers: cfg.headers,
    hint: cfg.hint || '',
    onboardingOnly: cfg.onboardingOnly,
  }));
}
